import { PayloadAction, createSlice } from '@reduxjs/toolkit'

// general imports
import {
  ApiRequestDataType,
  ApiRequestStatus,
  StoredErrorResponseType,
} from '../../../../types/api/api.types'
import {
  Transaction,
  TransactionPropType,
} from '../../../../types/molecules/transaction-table.molecule'
import { UploadedFilePropType } from '../../../../types/molecules/uploaded-files-table.molecule.type'
import {
  deleteTransactionFileThunk,
  getUploadedTransactionFilesThunk,
  processFileTransactionThunk,
  sendInitiatorOtpThunk,
  uploadTransactionFileThunk,
  verifyInitiatorOtpThunk,
} from '../thunk/transaction.thunk'

export type getAllTransactionsState = {
  // eslint-disable-next-line
  requestResponse: ApiRequestDataType<any>
  otpResponse: ApiRequestDataType<any>
  transactions: Transaction[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uploadedTransactionFiles: UploadedFilePropType[]
  selectedTransactionId: string
}

export const initialState: getAllTransactionsState = {
  requestResponse: {
    status: ApiRequestStatus.IDLE,
    data: null,
  },
  otpResponse: {
    status: ApiRequestStatus.IDLE,
    data: null,
  },
  transactions: [],
  uploadedTransactionFiles: [],
  selectedTransactionId: '',
}

const transactionSlice = createSlice({
  name: 'transactionSlice',
  initialState: initialState,
  reducers: {
    resetTransaction: (state) => {
      state.requestResponse.status = ApiRequestStatus.IDLE
      state.requestResponse.data = null
    },
    setSelectedTransactionId: (state, action: PayloadAction<string>) => {
      state.selectedTransactionId = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUploadedTransactionFilesThunk.pending, (state) => {
        state.requestResponse.status = ApiRequestStatus.PENDING
        state.requestResponse.data = null
        state.requestResponse.error = {} as StoredErrorResponseType
      })
      .addCase(getUploadedTransactionFilesThunk.fulfilled, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.FULFILLED
        state.uploadedTransactionFiles = action.payload
      })
      .addCase(getUploadedTransactionFilesThunk.rejected, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.REJECTED
        state.requestResponse.error = action.payload as StoredErrorResponseType
      })
      .addCase(sendInitiatorOtpThunk.pending, (state) => {
        state.requestResponse.status = ApiRequestStatus.PENDING
        state.requestResponse.data = null
        state.requestResponse.error = {} as StoredErrorResponseType
      })
      .addCase(sendInitiatorOtpThunk.fulfilled, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.FULFILLED
        state.requestResponse.data = action.payload
      })
      .addCase(sendInitiatorOtpThunk.rejected, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.REJECTED
        state.requestResponse.error = action.payload as StoredErrorResponseType
      })
      .addCase(verifyInitiatorOtpThunk.pending, (state) => {
        state.otpResponse.status = ApiRequestStatus.PENDING
        state.otpResponse.data = null
        state.otpResponse.error = {} as StoredErrorResponseType
      })
      .addCase(verifyInitiatorOtpThunk.fulfilled, (state, action) => {
        state.otpResponse.status = ApiRequestStatus.FULFILLED
        state.otpResponse.data = action.payload
      })
      .addCase(verifyInitiatorOtpThunk.rejected, (state, action) => {
        state.otpResponse.status = ApiRequestStatus.REJECTED
        state.otpResponse.error = action.payload as StoredErrorResponseType
      })
      .addCase(processFileTransactionThunk.pending, (state) => {
        state.requestResponse.status = ApiRequestStatus.PENDING
        state.requestResponse.data = null
        state.requestResponse.error = {} as StoredErrorResponseType
      })
      .addCase(processFileTransactionThunk.fulfilled, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.FULFILLED
        state.requestResponse.data = action.payload
      })
      .addCase(processFileTransactionThunk.rejected, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.REJECTED
        state.requestResponse.error = action.payload as StoredErrorResponseType
      })
      .addCase(uploadTransactionFileThunk.pending, (state) => {
        state.requestResponse.status = ApiRequestStatus.PENDING
        state.requestResponse.data = null
        state.requestResponse.error = {} as StoredErrorResponseType
      })
      .addCase(uploadTransactionFileThunk.fulfilled, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.FULFILLED
        state.requestResponse.data = action.payload
      })
      .addCase(uploadTransactionFileThunk.rejected, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.REJECTED
        state.requestResponse.error = action.payload as StoredErrorResponseType
      })
      .addCase(deleteTransactionFileThunk.pending, (state) => {
        state.requestResponse.status = ApiRequestStatus.PENDING
        state.requestResponse.data = null
        state.requestResponse.error = {} as StoredErrorResponseType
      })
      .addCase(deleteTransactionFileThunk.fulfilled, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.FULFILLED
        state.requestResponse.data = action.payload
      })
      .addCase(deleteTransactionFileThunk.rejected, (state, action) => {
        state.requestResponse.status = ApiRequestStatus.REJECTED
        state.requestResponse.error = action.payload as StoredErrorResponseType
      })
  },
})

export const { resetTransaction, setSelectedTransactionId } = transactionSlice.actions
export default transactionSlice.reducer
